import { useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiChevronRight,
  FiSearch,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const today = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(
    new Date(),
  );

export default function CreateEmployeeAdvancePage() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    amount: "",
    issuedOn: today(),
    paymentMode: "CASH",
    paymentReference: "",
    reason: "",
  });

  useEffect(() => {
    if (!accessToken || !employeeSearch.trim()) {
      setEmployees([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const result = await apiRequest(
          `/employees?search=${encodeURIComponent(employeeSearch.trim())}`,
          { accessToken },
        );
        setEmployees(Array.isArray(result) ? result : result?.data || []);
      } catch {
        setEmployees([]);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [accessToken, employeeSearch]);

  const amountPreview = useMemo(() => Number(form.amount || 0), [form.amount]);

  const submit = async (event) => {
    event.preventDefault();
    if (!selectedEmployee) {
      setError("Select the employee who is receiving this advance.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await apiRequest("/employee-advances", {
        method: "POST",
        accessToken,
        body: {
          employeeId: selectedEmployee.id,
          amount: Number(form.amount),
          issuedOn: form.issuedOn,
          paymentMode: form.paymentMode,
          paymentReference: form.paymentReference.trim() || undefined,
          reason: form.reason.trim(),
        },
      });
      navigate("/admindashboard/employee-advances");
    } catch (requestError) {
      setError(requestError.message || "Unable to record the advance.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto space-y-6 pb-10">
      <button
        type="button"
        onClick={() => navigate("/admindashboard/employee-advances")}
        className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 transition hover:text-gray-950"
      >
        <FiArrowLeft /> Back to advances
      </button>

      <header>
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary-700">
          Employee advances
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-gray-950">
          Add employee advance
        </h1>
        <p className="mt-2 text-sm leading-6 text-gray-500">
          Record money issued to an employee. The amount remains outstanding
          until it is recovered manually or through payroll.
        </p>
      </header>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-primary-100 text-sm font-bold text-primary-800">
                1
              </span>
              <div>
                <h2 className="font-bold text-gray-950">Select employee</h2>
                <p className="text-sm text-gray-500">
                  Search by employee name or code.
                </p>
              </div>
            </div>

            {selectedEmployee ? (
              <div className="flex flex-col gap-4 rounded-xl border border-primary-200 bg-primary-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-bold text-primary-950">
                    {selectedEmployee.fullName}
                  </p>
                  <p className="mt-1 text-sm text-primary-800">
                    {selectedEmployee.employeeCode}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedEmployee(null)}
                  className="text-sm font-semibold text-primary-800 underline underline-offset-4"
                >
                  Change employee
                </button>
              </div>
            ) : (
              <div className="relative">
                <FiSearch
                  className="absolute left-3.5 top-3 text-gray-400"
                  size={18}
                />
                <input
                  autoFocus
                  value={employeeSearch}
                  onChange={(event) => setEmployeeSearch(event.target.value)}
                  placeholder="Start typing a name or employee code"
                  className="min-h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm outline-none transition placeholder:text-gray-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                />
                {employeeSearch.trim() ? (
                  <div className="absolute z-10 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
                    {employees.length ? (
                      employees.map((employee) => (
                        <button
                          key={employee.id}
                          type="button"
                          onClick={() => {
                            setSelectedEmployee(employee);
                            setEmployeeSearch("");
                            setEmployees([]);
                          }}
                          className="flex w-full items-center justify-between border-b border-gray-100 px-4 py-3.5 text-left last:border-b-0 hover:bg-gray-50"
                        >
                          <span>
                            <span className="block text-sm font-semibold text-gray-900">
                              {employee.fullName}
                            </span>
                            <span className="mt-0.5 block text-xs text-gray-500">
                              {employee.employeeCode}
                            </span>
                          </span>
                          <FiChevronRight className="text-gray-400" />
                        </button>
                      ))
                    ) : (
                      <p className="px-4 py-4 text-sm text-gray-500">
                        No active employee found.
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-5 flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-primary-100 text-sm font-bold text-primary-800">
                2
              </span>
              <div>
                <h2 className="font-bold text-gray-950">Advance details</h2>
                <p className="text-sm text-gray-500">
                  Enter the amount and how it was issued.
                </p>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Advance amount" required>
                <input
                  required
                  min="0.01"
                  step="0.01"
                  type="number"
                  value={form.amount}
                  onChange={(event) =>
                    setForm({ ...form, amount: event.target.value })
                  }
                  placeholder="0.00"
                />
              </Field>
              <Field label="Issued on" required>
                <input
                  required
                  type="date"
                  value={form.issuedOn}
                  onChange={(event) =>
                    setForm({ ...form, issuedOn: event.target.value })
                  }
                />
              </Field>
              <Field label="Payment mode" required>
                <select
                  value={form.paymentMode}
                  onChange={(event) =>
                    setForm({ ...form, paymentMode: event.target.value })
                  }
                >
                  <option value="CASH">Cash</option>
                  <option value="BANK_TRANSFER">Bank transfer</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
              </Field>
              <Field label="Reference number">
                <input
                  value={form.paymentReference}
                  onChange={(event) =>
                    setForm({ ...form, paymentReference: event.target.value })
                  }
                  placeholder="Optional UTR, cheque, or voucher no."
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Reason for advance" required>
                  <textarea
                    required
                    value={form.reason}
                    onChange={(event) =>
                      setForm({ ...form, reason: event.target.value })
                    }
                    placeholder="For example: medical emergency, festival advance, or travel expense"
                  />
                </Field>
              </div>
            </div>
          </section>
        </div>

        <aside className="h-fit rounded-2xl border border-primary-100 bg-primary-50/50 p-5 shadow-sm lg:sticky lg:top-6">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-700">
            Review
          </p>
          <p className="mt-3 text-sm text-gray-600">Employee</p>
          <p className="mt-1 font-bold text-gray-950">
            {selectedEmployee?.fullName || "Not selected"}
          </p>
          {selectedEmployee ? (
            <p className="text-sm text-gray-500">
              {selectedEmployee.employeeCode}
            </p>
          ) : null}
          <div className="my-5 border-t border-primary-100" />
          <p className="text-sm text-gray-600">Amount to issue</p>
          <p className="mt-1 text-3xl font-bold tracking-tight text-primary-950">
            ₹
            {amountPreview.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          <div className="mt-5 flex gap-2 rounded-xl bg-white/80 p-3 text-xs leading-5 text-gray-600">
            <FiCheckCircle className="mt-0.5 shrink-0 text-primary-700" />
            This will create an open advance. Recording a repayment or payroll
            recovery reduces its balance.
          </div>
          <button
            disabled={saving || !selectedEmployee}
            className="mt-6 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-primary-800 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving advance…" : "Save employee advance"}
          </button>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block text-sm font-semibold text-gray-800">
      <span className="mb-1.5 block">
        {label}
        {required ? <span className="ml-1 text-red-600">*</span> : null}
      </span>
      <div className="[&_input]:min-h-11 [&_input]:w-full [&_input]:rounded-xl [&_input]:border [&_input]:border-gray-200 [&_input]:px-3.5 [&_input]:text-sm [&_input]:font-normal [&_input]:outline-none [&_input]:transition [&_input]:focus:border-primary-500 [&_input]:focus:ring-4 [&_input]:focus:ring-primary-100 [&_select]:min-h-11 [&_select]:w-full [&_select]:rounded-xl [&_select]:border [&_select]:border-gray-200 [&_select]:bg-white [&_select]:px-3.5 [&_select]:text-sm [&_select]:font-normal [&_select]:outline-none [&_select]:focus:border-primary-500 [&_select]:focus:ring-4 [&_select]:focus:ring-primary-100 [&_textarea]:min-h-28 [&_textarea]:w-full [&_textarea]:resize-y [&_textarea]:rounded-xl [&_textarea]:border [&_textarea]:border-gray-200 [&_textarea]:px-3.5 [&_textarea]:py-3 [&_textarea]:text-sm [&_textarea]:font-normal [&_textarea]:outline-none [&_textarea]:focus:border-primary-500 [&_textarea]:focus:ring-4 [&_textarea]:focus:ring-primary-100">
        {children}
      </div>
    </label>
  );
}
